/**
 * 407 (DECISIONS 407; DIRECTION §0.14 step 2): the viewmodel — at the eye, the player's own arms and the gun in hand, drawn
 * in a pass of their own after the world: its own camera (a near plane at a centimetre, its own field of view), the depth
 * cleared first, so the gun never sinks into a wall at a doorway and the world's wide view doesn't stretch it. What it
 * draws is the own figure as personModel posed it this frame — the aim, the low ready, the sprint's port arms, the reload,
 * the swap — so every move the figure makes is the viewmodel's, with nothing posed twice. The figure stays hidden from the
 * world's pass (400 step 1); here only the parts on VIEWMODEL_LAYER draw (the arms, the hands and what they hold, the `aim`
 * group and the gun on it), lit by copies of the world's lights. The host names the figure each frame it is at the eye
 * (`showViewmodel`), else none (`hideViewmodel`); with none the pass costs nothing. Presentation only.
 */
import * as THREE from 'three';
import { Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import { glasslessOf } from './seatInsides';

/** The render layer the viewmodel's parts draw on (beside the world's 0: the world's camera never sees this one alone). */
export const VIEWMODEL_LAYER = 1;
/**
 * The pass's camera: its field of view (degrees; the eye's world view is 70), its near plane (person units: a centimetre),
 * and where it sits from the world camera (person units, in the camera's frame: right, up, back) — the eye drawn a little
 * behind and over the stock, so the gun sits low and to the strong side as a shooter's does.
 */
export const VIEWMODEL = {
  fov: 60, near: 0.01, far: 4, offset: { x: 0, y: 0.04, z: 0.06 },
  /**
   * The moves seen from the eye: the figure's reload, its low carry and its swap take the gun below a 60° view, and the
   * sprint's port arms carries it up across the view's middle; so the pass's camera tips after the muzzle when it drops
   * past `below` (radians under the view's centre) or rises past `above`, by `share` of the excess, eased over `ease`
   * seconds — a low gun comes up into view, a high one down out of the middle. The world's camera and the aim are
   * untouched. The aim's own muzzle sits ~13° under the centre, so the aim is drawn as posed.
   */
  follow: { below: 0.3, above: 0.15, share: 0.85, ease: 0.12 },
  /**
   * 73.9 step 3: the kit's actions (a throw, a bandage, the dig, the slate) followed closer than the gun — the focus (the
   * strong hand, the blade, the screen) kept within `below` under the view's centre or `above` over it, by `share` of the
   * excess (the same ease). The gun's own moves keep `follow`.
   */
  kit: { below: 0.12, above: 0.2, share: 0.9 },
  /**
   * The fill (the coordinator: the gun near-black at night): a light from over and behind the eye on the viewmodel alone,
   * as strong as the world's light here falls short of `day` (each light by its colour's luminance, and from overhead: a
   * directional's by how far above the ground's plane it shines, the sky's half of the hemisphere by its turn up — at night
   * the sun still shines, from under the ground; the day's ≈ 2), times `most` at full dark, times the dial (the Lab's
   * `eyeFill`, 0 … 1; 1 by default). By day it adds next to nothing; the world's own light shades the gun as before.
   */
  fill: { day: 2, most: 2.4, from: { x: -0.4, y: 1, z: 0.6 } },
};
const PERSON_UNITS = 1.8;

interface Shown { subject: THREE.Object3D; parts: readonly THREE.Object3D[]; from: THREE.PerspectiveCamera; person: number; strong: 1 | -1; extras: readonly THREE.Object3D[]; muzzle?: (out: THREE.Vector3) => boolean | THREE.Vector3 }
let shown: Shown | null = null;
let fillDial: number | null = null;
/** The Lab's dial (0 … 1; `null` goes back to the page's `?eyefill=` percent, else full). */
export function setViewmodelFill(dial: number | null): void { fillDial = dial; }
function fillOf(): number {
  if (fillDial !== null) return fillDial;
  try { const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('eyefill') : null; return q !== null && Number.isFinite(Number(q)) ? Math.max(0, Number(q)) / 100 : 1; } catch { return 1; }
}
/** The fill's intensity for the world's light `level` (its lights' summed intensity × luminance) at the dial `dial`. Pure. */
export function viewmodelFill(level: number, dial: number): number {
  const f = VIEWMODEL.fill;
  return Math.max(0, 1 - level / f.day) * f.most * dial;
}

/**
 * At the eye this frame: `subject` the own figure's holder (hidden from the world's pass), `parts` the subtrees to draw (the
 * arms and the aim group), `from` the world's camera, `person` a person's height in world units, `strong` +1 for a right
 * hand, −1 for a left (the offset mirrors). `extras`: world objects drawn with it, on VIEWMODEL_LAYER alone (the own shot's
 * flash at the drawn muzzle: placed in the world, it sits on the viewmodel's gun, as both are the same gun's). `muzzle`: the
 * gun in hand's muzzle (world, into its argument), which the pass's camera follows down (VIEWMODEL.follow); 73.9 step 3: or
 * the kit's focus, when it returns true (VIEWMODEL.kit).
 */
export function showViewmodel(subject: THREE.Object3D, parts: readonly THREE.Object3D[], from: THREE.PerspectiveCamera, person: number, strong: 1 | -1 = 1, extras: readonly THREE.Object3D[] = [], muzzle?: (out: THREE.Vector3) => boolean | THREE.Vector3): void {
  shown = { subject, parts, from, person, strong, extras, ...(muzzle ? { muzzle } : {}) };
}
/** Not at the eye (or no figure): the pass draws nothing. */
export function hideViewmodel(): void { shown = null; }
/** Whether a figure is shown this frame (the tests', the host's). */
export function viewmodelShown(): boolean { return shown !== null; }

const vmScene = new THREE.Scene();
vmScene.matrixWorldAutoUpdate = false;   // (the subject's matrices are its own walk's, below)
const camera = new THREE.PerspectiveCamera(VIEWMODEL.fov, 1, VIEWMODEL.near, VIEWMODEL.far);
camera.layers.set(VIEWMODEL_LAYER);
/** The world's lights, copied into the pass's scene each draw (its sun turns with the clock and skyHaze tints it). */
const lightCopies = new Map<THREE.Light, THREE.Light>();
const fill = new THREE.DirectionalLight(0xdde4f0, 0);
fill.layers.enable(VIEWMODEL_LAYER);
vmScene.add(fill, fill.target);
let lightsOf: THREE.Object3D | null = null;

function bindLights(world: THREE.Object3D): void {
  if (lightsOf === world) return;
  for (const copy of lightCopies.values()) vmScene.remove(copy);
  lightCopies.clear();
  for (const o of world.children) {
    const l = o as THREE.Light;
    if (!l.isLight) continue;
    const copy = (l as THREE.HemisphereLight).isHemisphereLight ? new THREE.HemisphereLight() : (l as THREE.DirectionalLight).isDirectionalLight ? new THREE.DirectionalLight() : (l as THREE.AmbientLight).isAmbientLight ? new THREE.AmbientLight() : null;
    if (!copy) continue;
    copy.layers.enable(VIEWMODEL_LAYER);
    vmScene.add(copy);
    if ((copy as THREE.DirectionalLight).isDirectionalLight) vmScene.add((copy as THREE.DirectionalLight).target);
    lightCopies.set(l, copy);
  }
  lightsOf = world;
}

/** Copy the world's lights; returns their level here, with `up` the ground's up (worldLight). */
function copyLights(up: THREE.Vector3): number {
  let level = 0;
  for (const [l, c] of lightCopies) {
    if (l.visible) level += worldLight(l, up);
    c.visible = l.visible; c.color.copy(l.color); c.intensity = l.intensity;
    if ((l as THREE.HemisphereLight).isHemisphereLight) (c as THREE.HemisphereLight).groundColor.copy((l as THREE.HemisphereLight).groundColor);
    // a directional (and the hemisphere's up) is its position's way to its target: in world terms, the same here
    c.position.setFromMatrixPosition(l.matrixWorld);
    if ((l as THREE.DirectionalLight).isDirectionalLight) (c as THREE.DirectionalLight).target.position.setFromMatrixPosition((l as THREE.DirectionalLight).target.matrixWorld);
    c.updateMatrixWorld();
    if ((c as THREE.DirectionalLight).isDirectionalLight) (c as THREE.DirectionalLight).target.updateMatrixWorld();
  }
  return level;
}
const lum = (c: THREE.Color) => 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
const _ld = new THREE.Vector3(), _lt = new THREE.Vector3();
/** A light's share of the light here: its intensity × its colour's luminance, a directional's by its height over the ground's plane (`up`), the hemisphere's sky and ground by their turn up. */
export function worldLight(l: THREE.Light, up: THREE.Vector3): number {
  if ((l as THREE.AmbientLight).isAmbientLight) return l.intensity * lum(l.color);
  _ld.setFromMatrixPosition(l.matrixWorld);
  if ((l as THREE.DirectionalLight).isDirectionalLight) _ld.sub(_lt.setFromMatrixPosition((l as THREE.DirectionalLight).target.matrixWorld));
  const u = _ld.lengthSq() > 0 ? _ld.normalize().dot(up) : 1;
  if ((l as THREE.HemisphereLight).isHemisphereLight) return l.intensity * (lum(l.color) * (1 + u) + lum((l as THREE.HemisphereLight).groundColor) * (1 - u)) / 2;
  return l.intensity * lum(l.color) * Math.max(0, u);
}

const _off = new THREE.Vector3(), _mz = new THREE.Vector3(), _up = new THREE.Vector3();
/** The pass camera's tip down after the muzzle (radians), eased, and the clock it eased on. */
let tip = 0, tipAt = -1;

/** How far the pass's camera tips down (negative: up) for a muzzle `elevation` radians off the view's centre (negative: under it). Pure. */
export function viewmodelTip(elevation: number, f: { below: number; above: number; share: number } = VIEWMODEL.follow): number {
  return elevation < -f.below ? (-f.below - elevation) * f.share : elevation > f.above ? -(elevation - f.above) * f.share : 0;
}

/**
 * 411 (DECISIONS 411): at an enclosed seat's eye this frame — `vehicle` the own vehicle's root (hidden by the host from the
 * world's pass), `inside` the seat's inside (rendering/seatInsides.ts: in the hull's frame), `hull` the frame it rides, `from`
 * the world's camera. The pass draws them over the world with the world camera's own view: first the vehicle, its glass
 * dropped (see-through from inside), then the inside, each with the depth cleared — so neither the world nor the hull's
 * faces near the eye cut through the inside.
 */
export function showSeatInside(vehicle: THREE.Object3D, hull: THREE.Object3D, inside: THREE.Mesh, from: THREE.PerspectiveCamera): void {
  seat = { vehicle, hull, inside, from };
}
export function hideSeatInside(): void { seat = null; }
/** Whether an enclosed seat's inside is drawn this frame (the tests', the host's). */
export function seatInsideShown(): boolean { return seat !== null; }
let seat: { vehicle: THREE.Object3D; hull: THREE.Object3D; inside: THREE.Mesh; from: THREE.PerspectiveCamera } | null = null;
const seatCam = new THREE.PerspectiveCamera();
seatCam.matrixAutoUpdate = false; seatCam.matrixWorldAutoUpdate = false;
const swapped: Array<[THREE.Mesh, THREE.Material]> = [];
/** 411: an enclosed seat's dome lamp — the pass's fill, from over the eye toward a step ahead (`back` < 0: ahead in the camera's frame). */
export const SEAT_LAMP = { intensity: 1.1, back: -1 };
const _lampM = new THREE.Matrix4();

function drawSeatInside(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget | null): void {
  const s = seat;
  if (!s || !s.vehicle.parent) return;
  // the world camera's view exactly (the inside sits in the world: no offset, no own lens)
  seatCam.matrixWorld.copy(s.from.matrixWorld); seatCam.matrixWorldInverse.copy(s.from.matrixWorldInverse);
  seatCam.projectionMatrix.copy(s.from.projectionMatrix); seatCam.projectionMatrixInverse.copy(s.from.projectionMatrixInverse);
  let world: THREE.Object3D = s.vehicle.parent;
  while (world.parent) world = world.parent;
  bindLights(world);
  copyLights(_up.copy(s.from.up).normalize());
  fill.intensity = 0;   // (the vehicle: the world's light)
  vmScene.fog = (world as THREE.Scene).fog ?? null;
  // the vehicle lent to the pass's scene (its parent the world's scene at the origin: its matrices as in the world), its glass dropped
  const parent = s.vehicle.parent, wasVisible = s.vehicle.visible, wasAuto = renderer.autoClear;
  vmScene.add(s.vehicle);
  s.vehicle.visible = true;
  s.vehicle.updateMatrixWorld(true);
  s.vehicle.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh && !Array.isArray(m.material)) { const g = glasslessOf(m.material); if (g !== m.material) { swapped.push([m, m.material]); m.material = g; } } });
  renderer.autoClear = false;
  renderer.setRenderTarget(target);
  renderer.clearDepth();
  renderer.render(vmScene, seatCam);
  for (const [m, mat] of swapped) m.material = mat;
  swapped.length = 0;
  s.vehicle.visible = wasVisible;
  parent.add(s.vehicle);
  // the inside, on the hull's frame, over the vehicle; lit by the world and its dome lamp (the fill, from over the eye: a
  // closed bay or a station reads at noon and at night)
  s.inside.matrix.copy(s.hull.matrixWorld);
  s.inside.matrixWorld.copy(s.hull.matrixWorld);
  vmScene.add(s.inside);
  fill.intensity = SEAT_LAMP.intensity;
  fill.position.setFromMatrixPosition(seatCam.matrixWorld).add(_off.copy(s.from.up).normalize());
  fill.target.position.setFromMatrixPosition(seatCam.matrixWorld).add(_off.set(0, 0, SEAT_LAMP.back).applyMatrix4(_lampM.extractRotation(seatCam.matrixWorld)));
  fill.updateMatrixWorld(); fill.target.updateMatrixWorld();
  renderer.clearDepth();
  renderer.render(vmScene, seatCam);
  vmScene.remove(s.inside);
  fill.intensity = 0;
  renderer.autoClear = wasAuto;
}

/**
 * Draw the viewmodel into `target` (the composer's read buffer; null: the screen) over what is there: the depth cleared, the
 * colour kept. Nothing when no figure is shown. 411: an enclosed seat's inside first, when one is shown.
 */
export function drawViewmodel(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget | null): void {
  drawSeatInside(renderer, target);
  const s = shown;
  if (!s || !s.subject.parent) return;
  for (const part of s.parts) part.traverse(o => o.layers.enable(VIEWMODEL_LAYER));   // (a gun swapped in, a grenade built: each frame, a few dozen nodes)
  // the world's camera, the eye drawn a step back and up; the pass's own lens
  const from = s.from, k = s.person / PERSON_UNITS;
  camera.position.setFromMatrixPosition(from.matrixWorld);
  camera.quaternion.setFromRotationMatrix(from.matrixWorld);
  camera.position.add(_off.set(VIEWMODEL.offset.x * s.strong, VIEWMODEL.offset.y, VIEWMODEL.offset.z).multiplyScalar(k).applyQuaternion(camera.quaternion));
  // the moves seen from the eye: tip after a muzzle gone low (the reload, the carry) or high (the sprint's port arms), eased
  let want = 0;
  if (s.muzzle) {
    camera.updateMatrixWorld();
    const kit = s.muzzle(_mz) === true, m = _mz.applyMatrix4(camera.matrixWorldInverse);
    if (m.z < 0) want = viewmodelTip(Math.atan2(m.y, -m.z), kit ? VIEWMODEL.kit : VIEWMODEL.follow);
  }
  const now = performance.now() / 1000, dt = tipAt < 0 ? 1 : Math.min(0.25, now - tipAt);
  tipAt = now;
  tip += (want - tip) * (1 - Math.exp(-dt / VIEWMODEL.follow.ease));
  if (Math.abs(tip) > 1e-4) camera.rotateX(-tip);
  const aspect = from.aspect, near = VIEWMODEL.near * k, far = VIEWMODEL.far * k;
  if (camera.aspect !== aspect || camera.near !== near || camera.far !== far) { camera.aspect = aspect; camera.near = near; camera.far = far; camera.updateProjectionMatrix(); }
  camera.updateMatrixWorld();
  let world: THREE.Object3D = s.subject.parent;
  while (world.parent) world = world.parent;
  bindLights(world);
  // the fill: over and behind the eye (in the pass camera's frame), as the world's light falls short of the day's
  fill.intensity = viewmodelFill(copyLights(_up.copy(from.up).normalize()), fillOf());   // (always in the scene, 0 by day: the lights' count keys the programs)
  fill.position.copy(camera.position).add(_off.set(VIEWMODEL.fill.from.x * s.strong, VIEWMODEL.fill.from.y, VIEWMODEL.fill.from.z).applyQuaternion(camera.quaternion));
  fill.target.position.copy(camera.position);
  fill.updateMatrixWorld(); fill.target.updateMatrixWorld();
  vmScene.fog = (world as THREE.Scene).fog ?? null;   // (the world's: the same programs, no compile on the first frame at the eye)
  // the subject and the extras, lent to the pass's scene for the draw (their world matrices as in the world: both scenes sit
  // at the origin)
  const parent = s.subject.parent, wasVisible = s.subject.visible, wasAuto = renderer.autoClear;
  const lent = s.extras.filter(o => o.parent), homes = lent.map(o => o.parent!);
  vmScene.add(s.subject);
  for (const o of lent) { vmScene.add(o); o.updateMatrixWorld(); }
  s.subject.visible = true;
  s.subject.updateMatrixWorld(true);
  renderer.autoClear = false;
  renderer.setRenderTarget(target);
  renderer.clearDepth();
  renderer.render(vmScene, camera);
  renderer.autoClear = wasAuto;
  s.subject.visible = wasVisible;
  parent.add(s.subject);
  lent.forEach((o, i) => homes[i].add(o));
}

/** The composer's pass (after the world's RenderPass, before the bloom): draws into the read buffer, no swap. */
export class ViewmodelPass extends Pass {
  constructor() { super(); this.needsSwap = false; }
  render(renderer: THREE.WebGLRenderer, _write: THREE.WebGLRenderTarget, read: THREE.WebGLRenderTarget): void {
    drawViewmodel(renderer, this.renderToScreen ? null : read);
  }
}
